import React from 'react';
import { ArrowUpDown } from 'lucide-react';
import type { SortConfig } from '../../../hooks/useSort';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { formatDecimal } from '../../../utils/format';
import type { RoastingReportRow, RoastingReportSummary } from '../../../services/report.service';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastingReportSectionProps {
  summary: RoastingReportSummary;
  rows: RoastingReportRow[];
  loading: boolean;
  error: string | null;
  sortConfig: SortConfig<RoastingReportRow> | null;
  onSort: (key: keyof RoastingReportRow) => void;
}

export const RoastingReportSection: React.FC<RoastingReportSectionProps> = ({
  summary,
  rows,
  loading,
  error,
  sortConfig,
  onSort,
}) => {
  const { t } = useLanguage();
  return (
    <section className="space-y-4" data-test-id="roasting-report-section">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roasting.totalBatches')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.totalBatches, 0)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roasting.inputWeightKg')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.totalInputWeight, 2)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roasting.outputWeightKg')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.totalOutputWeight, 2)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roasting.weightLossKg')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.totalWeightLoss, 2)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roasting.averageLossPercent')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.averageLossPercent, 2)}%</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" data-test-id="roasting-report-table">
          <thead className="bg-gray-50">
            <tr>
              {[
                ['roastingDate', t('reports.export.columns.roastingDate')],
                ['batchNumber', t('reports.export.columns.batchNumber')],
                ['greenBeanCode', t('reports.export.columns.greenBeanCode')],
                ['greenBeanName', t('reports.export.columns.greenBeanName')],
                ['inputWeight', t('reports.roasting.inputKg')],
                ['outputWeight', t('reports.roasting.outputKg')],
                ['weightLossPercent', t('reports.roasting.lossPercent')],
              ].map(([key, label]) => (
                <th
                  key={key}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort(key as keyof RoastingReportRow)}
                >
                  <div className="flex items-center gap-1">
                    <span>{label}</span>
                    {sortConfig?.key === key && <ArrowUpDown className="h-3.5 w-3.5" />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingSpinner text={t('reports.roasting.loading')} size="sm" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-sm text-red-600">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-sm text-gray-500">
                  {t('reports.roasting.empty')}
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50" data-test-id={`roasting-row-${row.id}`}>
                  <td className="px-4 py-3 text-sm text-gray-900">{new Date(row.roastingDate).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.batchNumber}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.greenBeanCode}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.greenBeanName}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{formatDecimal(row.inputWeight, 2)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{formatDecimal(row.outputWeight, 2)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{formatDecimal(row.weightLossPercent, 2)}%</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
